import axios from 'axios';
import Cookies from 'js-cookie';
import tokenManager from './tokenManager';
import API_URL from './apiConfig';
import { refreshAccessToken } from './apiClient';

const API_KEY =  process.env.REACT_APP_AWS_API_KEY ;
const SESSION_EXPIRED_EVENT = 'auth:session-expired';
const TOKEN_REFRESH_STATUS_CODES = [401, 403];
const refreshableEndpoints = [
  '/auth/challenges/verify',
  '/user/me',
  '/ngo/me',
  '/pet/profile',
  '/pet/transfer',
  '/pet/source',
  '/pet/adoption',
  '/pet/medical',
  '/pet/recovery',
  '/pet/analysis',
  '/commerce',
  '/logistics',
  '/notifications',
];
let isRefreshing = false;
let failedQueue = [];

const notifySessionExpired = (reason = 'session_expired') => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(SESSION_EXPIRED_EVENT, {
        detail: { reason },
      })
    );
  }
};

const hadAuthenticatedContext = (requestConfig) => {
  const inMemoryToken = tokenManager.getAuthToken();
  const inMemoryUserId = tokenManager.getUserId();
  const authHeader = requestConfig?.headers?.Authorization || requestConfig?.headers?.authorization;

  return Boolean(inMemoryToken || inMemoryUserId || authHeader);
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const isRefreshableEndpoint = (url) => {
  if (!url) return false;
  return refreshableEndpoints.some((endpoint) => url.includes(endpoint));
};

const isTokenRefreshStatus = (status) => TOKEN_REFRESH_STATUS_CODES.includes(status);

const shouldAttemptTokenRefresh = (error, originalRequest, hadAuthContext) => {
  if (!originalRequest || originalRequest._retry || !hadAuthContext) {
    return false;
  }

  if (!isRefreshableEndpoint(originalRequest.url)) {
    return false;
  }

  if (isTokenRefreshStatus(error.response?.status)) {
    return true;
  }

  // API Gateway/Lambda 403s can be hidden from Axios as CORS/network errors
  // when the error response misses CORS headers. For authenticated DDD routes,
  // try one refresh before surfacing the failure.
  return Boolean(error.request && !error.response);
};

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Enable sending cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for additional configuration if needed
axiosInstance.interceptors.request.use(
  (config) => {
    // Always add x-api-key header for DDD API compatibility
    config.headers = config.headers || {};
    config.headers['x-api-key'] = API_KEY;
    
    // Try to get token from tokenManager first (in-memory, more secure)
    let token = tokenManager.getAuthToken();
    
    // Fallback to cookies for backward compatibility
    if (!token) {
      token = Cookies.get('token');
    }
    
    // Only add Authorization header if token exists and is not empty
    if (token && token.trim() !== '' && token !== 'undefined' && token !== 'null') {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If uploading FormData, let axios set the correct multipart boundary.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for token refresh and error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const shouldShowSessionExpiredPopup = hadAuthenticatedContext(originalRequest);

    if (shouldAttemptTokenRefresh(error, originalRequest, shouldShowSessionExpiredPopup)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();

        if (newToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return axiosInstance(originalRequest);
        }

        processQueue(new Error('Token refresh failed'), null);
        tokenManager.clear();
        if (shouldShowSessionExpiredPopup) {
          notifySessionExpired('refresh_failed');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        tokenManager.clear();
        if (shouldShowSessionExpiredPopup) {
          notifySessionExpired('refresh_exception');
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle common errors with detailed information
    if (error.response) {
      if (error.response.status === 401) {
        console.log('Please login again');
      } else {
        const errorDetails = {
          status: error.response.status,
          url: error.config?.url || 'unknown',
          method: error.config?.method?.toUpperCase() || 'unknown',
          origin: error.response.headers?.['x-error-origin'] || 'unknown',
          data: error.response.data
        };

        console.error(`API Error ${error.response.status}:`, errorDetails);
      }
    } else {
      console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
